import { APIRequest } from '@lynx/client-core/src/api'
import { MD5 } from 'crypto-js'
import { AuthCookie } from '@lynx/client-core/src/modules'
import * as QRCode from 'qrcode'
import { Action } from 'redux'
import { ThunkAction } from 'redux-thunk'
import { ApplicationState } from 'store'
import { subscribe, wsConnect } from 'store/messaging'
import { loginUnregisteredUser } from 'store/profile'
import {
	END_SESSION,
	LitbUser,
	MAKE_ADMIN,
	SessionActionTypes,
	SET_CONNECTED,
	SET_DOMAIN,
	SET_EDITOR,
	SET_PERCENT,
	SET_SUBSCRIBED,
	SET_USERS,
	SET_VNC_DETAILS,
	SubscribedState
} from './types'

export const makeAdmin = (isAdmin = false): SessionActionTypes => {
	return {
		type: MAKE_ADMIN,
		payload: isAdmin
	}
}

export const setConnected = (connected = false): SessionActionTypes => {
	return {
		type: SET_CONNECTED,
		payload: connected
	}
}

export const setSubscribe = (subscribed: SubscribedState): SessionActionTypes => {
	return {
		type: SET_SUBSCRIBED,
		payload: subscribed
	}
}

export const setEditor = (editor: string): SessionActionTypes => {
	return {
		type: SET_EDITOR,
		payload: editor
	}
}

export const setDomain = (domain: string): SessionActionTypes => {
	return {
		type: SET_DOMAIN,
		payload: domain
	}
}

export const setVNCDetails = (details: { username: string; password: string }): SessionActionTypes => {
	const { username, password } = details
	return {
		type: SET_VNC_DETAILS,
		payload: { username, password }
	}
}

export const setUsers = (users: LitbUser[]): SessionActionTypes => {
	return {
		type: SET_USERS,
		payload: users
	}
}

export const setPercent = (percent: { percent: number; status: string }): SessionActionTypes => {
	return {
		type: SET_PERCENT,
		payload: percent
	}
}

export const endConnection = (): SessionActionTypes => {
	return {
		type: END_SESSION
	}
}

export const makeReadOnly =
	(accessToken: string, channelId: string, displayName: string): ThunkAction<void, ApplicationState, null, Action<string>> =>
	async (dispatch, getState): Promise<void> => {
		const {
			config: { MESSAGING_WS_SERVER },
			profile: { loggedIn }
		} = getState()
		const socket = new WebSocket('wss://editor.lynxcloud.app/configure')

		socket.addEventListener('open', () => {
			socket.send(JSON.stringify({ command: 'share', shareHash: channelId }))
		})

		socket.addEventListener('message', (event) => {
			try {
				const data = JSON.parse(event.data)
				if (data['display']) {
					const { username, password } = data['display']
					dispatch(setVNCDetails({ username, password }))
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.log(err)
			}
		})

		const actualDisplayName = displayName
		if (!loggedIn) {
			await dispatch(loginUnregisteredUser(`${accessToken}`, actualDisplayName))
		}
		dispatch(wsConnect(`${MESSAGING_WS_SERVER}?token=${accessToken}&displayName=${actualDisplayName}`))
		await dispatch(subscribe(`${channelId}`))
		dispatch(makeAdmin(false))
	}

export const getUsers =
	(forced = false): ThunkAction<Promise<void>, ApplicationState, null, Action<string>> =>
	async (dispatch, getState): Promise<void> => {
		const {
			profile: { userId, displayName },
			session: { channelId, isAdmin, users }
		} = getState()
		let newUsers = await APIRequest.Channels.subscribers(channelId)
		if (newUsers && isAdmin) {
			newUsers = newUsers.filter((n) => n.isConnected)

			const userListWithAdmin = [
				{
					userId,
					displayName: `${displayName} (admin)`,
					isConnected: true
				}
			].concat(newUsers)

			if (forced || userListWithAdmin.length !== users.length) {
				dispatch(setUsers(userListWithAdmin))
			}
		}
	}

export const internalSubscribe =
	(
		driveItemId: string,
		fileName: string,
		userId: string,
		provider: string,
		paths: string,
		shareId: string,
		shareOwnerId: string,
		lynxCloudClient: string,
		messagingServer: string,
		ENVIRONMENT: string,
		MESSAGING_WS_SERVER: string
	): ThunkAction<void, ApplicationState, null, Action<string>> =>
	async (dispatch): Promise<void> => {
		const hash = MD5(`${driveItemId}-${userId}`).toString().toLowerCase()
		const channelId = `litb-${hash}`.substring(0, 31)

		const socket = new WebSocket('wss://editor.lynxcloud.app/configure')

		socket.addEventListener('open', (): void => {
			socket.send(JSON.stringify({ command: 'init', shareHash: channelId }))
		})

		const steps: { [step: string]: { percent: number; status: string } } = {
			queue: {
				percent: 11,
				status: 'In a queue'
			},
			user: {
				percent: 22,
				status: 'User is being created'
			},
			ownership: {
				percent: 33,
				status: 'Ownership is being set'
			},
			permissions: {
				percent: 44,
				status: 'Permissions are being set'
			},
			session: {
				percent: 55,
				status: 'Session is being started'
			},
			created: {
				percent: 66,
				status: 'User created'
			},
			'file.json': {
				percent: 77,
				status: 'File details is being set'
			},
			'auth.json': {
				percent: 88,
				status: 'Auth file is being created'
			},
			lynx: {
				percent: 100,
				status: 'LYNX is being started'
			},
			share: {
				percent: 100,
				status: 'Getting details'
			},
			fail: {
				percent: 100,
				status: 'Failed to start session'
			}
		}

		socket.addEventListener('message', async (event): Promise<void> => {
			try {
				const data = JSON.parse(event.data)
				if (data['step']) {
					if (steps[data['step']]) dispatch(setPercent(steps[data['step']]))
					const accessToken = APIRequest.getAccessToken()

					if (data['step'] === 'created') {
						const result = await APIRequest.LynxVnc.apply(driveItemId, provider, data['display'].username, shareId, shareOwnerId, paths)
						const { file, auth } = result.data
						socket.send(JSON.stringify({ command: 'login', deployType: ENVIRONMENT, files: { file, auth }, shareHash: channelId, beta: true }))
					}

					if (data['step'] === 'lynx') {
						const { username, password } = data['display']
						if (await APIRequest.Channels.exists(channelId)) {
							await APIRequest.Channels.subscribe(channelId)
						} else {
							await APIRequest.Channels.create('vnc', channelId, channelId)
						}

						const code = await APIRequest.Channels.generateCode(channelId)

						dispatch(wsConnect(`${MESSAGING_WS_SERVER}?token=${accessToken}&displayName=Admin&file=${driveItemId}`))
						dispatch(makeAdmin(true))

						const qr = await QRCode.toDataURL(`${lynxCloudClient}/join/${code}`, { width: 256 })

						dispatch(
							setSubscribe({
								driveItemId,
								channelId,
								fileName,
								userId,
								code,
								qr,
								url: `${lynxCloudClient}/join/${code}`,
								ws: `${messagingServer}?token=${accessToken}&displayName=Admin&file=${driveItemId}`,
								isAdmin: true,
								username,
								password
							})
						)
					}
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.log(err)
			}
		})
	}
